import React, { FC, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@/components/Select";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import GenericAlertModal from "@/components/AlertModal";
import { useAppTheme } from "@/utils/theme";
import { useGetPrograms } from "@/hooks/usePrograms";
import { useProgramsByUser } from "@/hooks/useUsers";
import { AssignedOrgUnits } from "@/domain/user.interface";

// Shown when a (program, org unit) pair is locked (canUncheck === false).
const LOCKED_OU_MESSAGE =
  "The participants of this org unit and program are only assigned to this user. Please move all participants related to this user to another officer before removing access to this org unit.";

interface TabAssignOUProps {
  programsOptions: Array<{ key: string; value: string }>;
  setSelectedOrgUnitIds: (ids: string[]) => void;
  selectedOrgUnitIds: string[];
  isEditMode?: boolean;
  hasAdminRole?: boolean;
  isSubmitting?: boolean;
  editUserId?: string;
  clearSubmitting?: () => void;
}

export const TabAssignOU: FC<TabAssignOUProps> = ({
  programsOptions,
  setSelectedOrgUnitIds,
  selectedOrgUnitIds,
  isEditMode = false,
  hasAdminRole,
  isSubmitting,
  editUserId,
  clearSubmitting,
}) => {
  const theme = useAppTheme();
  const { control, watch } = useFormContext();

  const { programs } = useGetPrograms({});

  // The edited user's own programs carry the per-(program, org unit) `canUncheck`
  // flag used to lock org units the user cannot unassign (AC08). Only relevant
  // in edit mode; a brand-new user has no participants, so nothing is locked.
  const { programs: assignedPrograms } = useProgramsByUser({
    userId: editUserId as string,
    sortType: "asc",
    enabled: isEditMode && !!editUserId,
  });

  // The program dropdown is a view switcher only — it controls which program's
  // org units are shown. The actual selection lives in the `assignedOrgUnits`
  // form field, which accumulates one entry per program the user has touched.
  const [currentProgramId, setCurrentProgramId] = useState("");

  const assignedOrgUnits = (watch("assignedOrgUnits") ||
    []) as AssignedOrgUnits[];
  const hasAnyOrgUnit = assignedOrgUnits.some(
    (entry) => entry.orgUnitIds.length > 0
  );

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!hasAdminRole && !hasAnyOrgUnit && isSubmitting) {
      setShowError(true);
      clearSubmitting?.();
    }
  }, [isSubmitting]);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: selectedOrgUnitIds,
    status: "ACTIVE",
  });

  useEffect(() => {
    if (selectedOrgUnitIds.length > 0) {
      refetchOrganizationUnits();
    }
  }, [selectedOrgUnitIds]);

  // On edit, default the dropdown to the first already-assigned program so its
  // existing org-unit selections are visible.
  useEffect(() => {
    if (
      isEditMode &&
      !currentProgramId &&
      assignedOrgUnits.length > 0 &&
      programs &&
      programs.length > 0
    ) {
      const firstProgramId = assignedOrgUnits[0].programId;
      setCurrentProgramId(firstProgramId);
      const orgUnitIds = programs.find(
        (program) => program._id === firstProgramId
      )?.orgUnitId;
      setSelectedOrgUnitIds(orgUnitIds || []);
    }
  }, [isEditMode, currentProgramId, assignedOrgUnits, programs]);

  return (
    <>
      {hasAdminRole ? (
        <Typography>
          The users with account admin role have access to all programs and org
          units.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
          }}
        >
          <Select
            handleChange={(value) => {
              const programId = value as string;
              setCurrentProgramId(programId);

              const orgUnitIds = programs?.find(
                (program) => program._id === programId
              )?.orgUnitId;

              setSelectedOrgUnitIds(orgUnitIds || []);
            }}
            data={programsOptions || []}
            value={currentProgramId}
            label="Programs"
            id="users-programs"
            isLoading={isLoadingOrganizationUnits}
            maxWidth={220}
          />

          {!currentProgramId ? null : isLoadingOrganizationUnits ? (
            <Card>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100px",
                }}
              >
                <CircularProgress />
              </CardContent>
            </Card>
          ) : organizationUnits && organizationUnits.length > 0 ? (
            <Card>
              <CardContent>
                <Controller
                  name="assignedOrgUnits"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => {
                    const value = (field.value || []) as AssignedOrgUnits[];
                    const currentEntry = value.find(
                      (entry) => entry.programId === currentProgramId
                    );
                    const currentAssignedProgram = assignedPrograms?.find(
                      (program) => program._id === currentProgramId
                    );

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          width: "100%",
                        }}
                      >
                        {organizationUnits?.map((unit) => {
                          const isChecked =
                            currentEntry?.orgUnitIds.includes(unit._id) ??
                            false;
                          // A locked (program, org unit) pair cannot be
                          // unassigned. We only lock currently-assigned units so
                          // the disabled state never blocks adding a new one.
                          const isLocked =
                            isEditMode &&
                            isChecked &&
                            currentAssignedProgram?.orgUnitIds.find(
                              (orgUnit) => orgUnit._id === unit._id
                            )?.canUncheck === false;

                          const checkbox = (
                            <FormControlLabel
                              key={unit._id}
                              sx={{ mx: "0", px: "8px" }}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  disabled={isLocked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const next = currentEntry
                                        ? value.map((entry) =>
                                            entry.programId === currentProgramId
                                              ? {
                                                  ...entry,
                                                  orgUnitIds: [
                                                    ...entry.orgUnitIds,
                                                    unit._id,
                                                  ],
                                                }
                                              : entry
                                          )
                                        : [
                                            ...value,
                                            {
                                              programId: currentProgramId,
                                              orgUnitIds: [unit._id],
                                            },
                                          ];

                                      field.onChange(next);
                                    } else {
                                      // Remove the org unit from its program
                                      // entry, dropping the entry if it empties.
                                      const next = value
                                        .map((entry) =>
                                          entry.programId === currentProgramId
                                            ? {
                                                ...entry,
                                                orgUnitIds:
                                                  entry.orgUnitIds.filter(
                                                    (id) => id !== unit._id
                                                  ),
                                              }
                                            : entry
                                        )
                                        .filter(
                                          (entry) => entry.orgUnitIds.length > 0
                                        );

                                      field.onChange(next);
                                    }
                                  }}
                                />
                              }
                              label={unit.name}
                            />
                          );

                          return isLocked ? (
                            <Tooltip
                              key={unit._id}
                              title={LOCKED_OU_MESSAGE}
                              placement="right"
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  width: "fit-content",
                                }}
                              >
                                {checkbox}
                              </Box>
                            </Tooltip>
                          ) : (
                            checkbox
                          );
                        })}
                      </Box>
                    );
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography sx={{ color: "text.secondary" }}>
                  No organizational units available for this program.
                </Typography>
              </CardContent>
            </Card>
          )}

          {showError && (
            <GenericAlertModal
              onCancel={() => setShowError(false)}
              onClick={() => setShowError(false)}
              title="Error"
              description="At least one organizational unit must be selected for this user."
              submitText="Ok"
              theme={theme}
            />
          )}
        </Box>
      )}
    </>
  );
};
